/**
 * **Run 19 §16 — coffee.**
 *
 * Balázs's figures of 16 October and Barna's two markups of 18 October are
 * between them enough to price coffee, so F7's question goes live. What
 * changes from every earlier draft is **where the price comes from**: not a
 * catalogue row any more. `coffee_dish_id` is retired. A cup of coffee is
 * not a dish that is made and packed; it is a machine standing on a table
 * for a day plus the beans and the milk that go through it, and those are
 * four register figures and two markups.
 *
 * So coffee is priced here, out of the register, as **two lines**:
 *
 *   * the **machine** line — a fixed cost per day, `machines ×
 *     coffee_machine_day × coffee_machine_markup`;
 *   * the **coffee** line — `cups × (coffee per cup + milk per cup) ×
 *     coffee_cup_markup`, shown as a price per cup.
 *
 * They are separate because they move on different things: one more guest
 * adds cups, the fifty-first guest adds a whole machine.
 *
 * **Nothing here holds a figure.** Every one of the eleven values is read
 * out of `public.params` by `coffeeParamsFrom`, and a register missing any
 * of them prices no coffee at all rather than guessing one — which is what
 * keeps the question off the page where the answer is not there.
 */

import {
  ONE,
  add,
  ceilToInteger,
  divide,
  fromDecimal,
  isZero,
  multiply,
  rational,
  roundHalfUpToInteger,
  toNumber,
  type Rational,
} from './rational.ts'
import type { Params } from './context.ts'

/** The register keys §16 names, in one list, so nothing else spells them. */
export const COFFEE_PARAM_KEYS = [
  'coffee_cups_am',
  'coffee_cups_pm',
  'coffee_machine_capacity',
  'coffee_machine_day',
  'coffee_machine_markup',
  'coffee_cost_kg',
  'coffee_cups_per_kg',
  'milk_cost_l',
  'milk_l_per_milk_drink',
  'coffee_milk_share',
  'coffee_cup_markup',
] as const

export interface CoffeeParams {
  /** Cups per guest at a morning serving. EXPERT midpoint of 1,1–1,2. */
  cupsAm: Rational
  /** Cups per guest at an afternoon serving. EXPERT midpoint of 0,6–0,7. */
  cupsPm: Rational
  /** Guests one machine serves in a 30-minute break. */
  machineCapacity: number
  /** Planteen's net cost of one machine for one day. */
  machineDayNet: number
  /** What the machine line is charged at, as a multiplier on that cost. */
  machineMarkup: Rational
  /** Planteen's net cost of a kilo of coffee, and the cups it makes. */
  coffeeCostPerKgNet: number
  cupsPerKg: number
  /** Planteen's net cost of a litre of milk. */
  milkCostPerLitreNet: number
  /** The milk poured into the jug for one milk drink. An espresso takes none. */
  milkLitresPerMilkDrink: Rational
  /** The share of cups that are milk drinks rather than espresso (A20). */
  milkShare: Rational
  /** What the coffee line is charged at, as a multiplier on the cup's cost. */
  cupMarkup: Rational
}

const read = (params: Params, key: string): string | null => {
  const value = params[key]
  if (value === undefined || value === null || String(value).trim() === '') return null
  return String(value)
}

/**
 * The eleven figures, or **null** where the register is missing any one of
 * them — which is how the coffee question stays hidden on a project that
 * has not been given them. One check hides the question, both lines and the
 * stored machine record together.
 */
export function coffeeParamsFrom(params: Params): CoffeeParams | null {
  const values: Record<string, string> = {}
  for (const key of COFFEE_PARAM_KEYS) {
    const value = read(params, key)
    if (value === null) return null
    values[key] = value
  }
  const exact = (key: string): Rational => fromDecimal(values[key] as string)
  const whole = (key: string): number => Number((values[key] as string).replace(',', '.'))
  const capacity = whole('coffee_machine_capacity')
  const cupsPerKg = whole('coffee_cups_per_kg')
  /* A capacity of nothing would divide by zero and a kilo that makes no cups
     would price a cup at infinity. Both are a register fault, and a fault is
     no coffee rather than a number nobody meant. */
  if (!Number.isFinite(capacity) || capacity <= 0) return null
  if (!Number.isFinite(cupsPerKg) || cupsPerKg <= 0) return null
  return {
    cupsAm: exact('coffee_cups_am'),
    cupsPm: exact('coffee_cups_pm'),
    machineCapacity: capacity,
    machineDayNet: whole('coffee_machine_day'),
    machineMarkup: exact('coffee_machine_markup'),
    coffeeCostPerKgNet: whole('coffee_cost_kg'),
    cupsPerKg,
    milkCostPerLitreNet: whole('milk_cost_l'),
    milkLitresPerMilkDrink: exact('milk_l_per_milk_drink'),
    milkShare: exact('coffee_milk_share'),
    cupMarkup: exact('coffee_cup_markup'),
  }
}

/** How many servings of each kind the day holds (§16's morning/afternoon). */
export interface CoffeeServings {
  morning: number
  afternoon: number
}

export interface CoffeeOffer {
  /** Whole cups. Rounded up: half a cup is not served. */
  cups: number
  /** Guests ÷ `coffee_machine_capacity`, rounded up. */
  machines: number
  /** Planteen's own cost of the machines for the day, net. Internal (§5.11). */
  machineCostNet: number
  /**
   * What **one** machine charges for the day, net, to the forint. This is
   * the unit price the machine line is built from, the way a cup's is —
   * §5.11's arithmetic is *quantity × net unit price*, and a line whose
   * unit price was the whole line's total would charge the machines twice.
   */
  machineUnitChargeNet: number
  /** What the machine line charges altogether, net: machines × the unit. */
  machineChargeNet: number
  /** Planteen's own cost of one cup — coffee plus the averaged milk. Internal. */
  cupCostNet: number
  /** What one cup charges, net, to the forint. This is the price shown. */
  cupChargeNet: number
  /** `cups × cupChargeNet`. */
  coffeeChargeNet: number
  /** The two lines together, net. */
  net: number
}

/**
 * The milk in an average cup, in litres: an espresso takes none and a milk
 * drink takes `milk_l_per_milk_drink`, so the average is that times the
 * share of cups that are milk drinks.
 */
export const milkLitresPerCup = (params: CoffeeParams): Rational =>
  multiply(params.milkLitresPerMilkDrink, params.milkShare)

/**
 * §16's whole arithmetic, in one function.
 *
 * `null` where there is no coffee to price: no servings at all, or no
 * guests. A zero-cup day is not a coffee line with nothing on it — it is no
 * coffee line.
 *
 * **Rounding.** The cups are rounded up once, at the end, because a cup is
 * what is served. The **price per cup** is rounded to the whole forint
 * before it is multiplied, which is §5.11's arithmetic — a category's net is
 * the sum of its lines' quantities times their net unit prices — and is what
 * stops the price the offer card prints per cup from disagreeing with the
 * line total beside it.
 */
export function coffeeOfferOf(
  args: { guests: number; servings: CoffeeServings },
  params: CoffeeParams,
): CoffeeOffer | null {
  const guests = Math.max(Math.round(args.guests), 0)
  const morning = Math.max(Math.round(args.servings.morning), 0)
  const afternoon = Math.max(Math.round(args.servings.afternoon), 0)
  if (guests < 1) return null
  if (morning + afternoon < 1) return null

  const perGuest = add(
    multiply(params.cupsAm, rational(morning)),
    multiply(params.cupsPm, rational(afternoon)),
  )
  if (isZero(perGuest)) return null
  const cups = ceilToInteger(multiply(rational(guests), perGuest))
  if (cups < 1) return null

  const machines = Math.max(Math.ceil(guests / params.machineCapacity), 1)
  const machineCostNet = machines * params.machineDayNet
  /* Rounded **per machine**, then multiplied — so the figure the offer card
     prints beside *Kávégép, {n} db* times the count is the line total, and
     §5.11's arithmetic holds for this line as it does for a category. */
  const machineUnitChargeNet = roundHalfUpToInteger(
    multiply(rational(params.machineDayNet), params.machineMarkup),
  )
  const machineChargeNet = machines * machineUnitChargeNet

  /* Coffee per cup: a kilo's cost over the cups a kilo makes. Milk per cup:
     the averaged litres times the litre price. Both exact until the cup
     price is rounded. */
  const coffeePerCup = divide(rational(params.coffeeCostPerKgNet), rational(params.cupsPerKg))
  const milkPerCup = multiply(milkLitresPerCup(params), rational(params.milkCostPerLitreNet))
  const cupCostExact = add(coffeePerCup, milkPerCup)
  const cupChargeNet = roundHalfUpToInteger(multiply(cupCostExact, params.cupMarkup))
  const coffeeChargeNet = cups * cupChargeNet

  return {
    cups,
    machines,
    machineCostNet,
    machineUnitChargeNet,
    machineChargeNet,
    cupCostNet: roundHalfUpToInteger(cupCostExact),
    cupChargeNet,
    coffeeChargeNet,
    net: machineChargeNet + coffeeChargeNet,
  }
}

/**
 * The cost side on its own, for the console's *rent or buy* report (§16's
 * machine record): machine-days, what they cost Planteen and what they
 * earned. Never shown to a customer (§5.11).
 */
export const coffeeMachineMargin = (offer: CoffeeOffer): number =>
  offer.machineChargeNet - offer.machineCostNet

/** A guard the tests read: the markup is a multiplier, never a percentage. */
export const coffeeMarkupIsMultiplier = (markup: Rational): boolean =>
  toNumber(markup) >= toNumber(ONE)
